import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useSearchParams, useNavigate, useLocation, Link } from "react-router-dom";
import { 
  Trash2, Edit, Plus, Search, AlertTriangle, Users as UsersIcon, 
  Building2, Landmark, Home, MapPinned, ArrowLeft, ArrowRight, Mail, Phone, 
  Clock, ShieldAlert, CheckCircle2, User, MapPin, Tag, Landmark as OfficeIcon, 
  PhoneCall, Calendar, MessageSquare, AlertCircle, ClipboardCheck, Loader2, ExternalLink, RefreshCw
} from "lucide-react";
import { api, asItems, formatApiError, formatArea, formatPrice, prefixUrl } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import Pagination from "@/components/Pagination";
import { cleanNoteText } from "@/lib/noteText";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const ROLE_COLORS = {
  admin: "bg-primary/10 text-primary border border-primary/20",
  agent: "bg-emerald-500/10 text-emerald-500 border border-emerald-500/20",
  office: "bg-amber-500/10 text-amber-500 border border-amber-500/20",
  buyer: "bg-muted text-muted-foreground border border-border",
  owner: "bg-indigo-500/10 text-indigo-500 border border-indigo-500/20",
};

const SUBTYPES = [
  // Apartment / Flat
  { value: "1_RK_STUDIO", label: "1 RK / Studio", category: "FLAT", group: "Apartment / Flat" },
  { value: "1_BHK", label: "1 BHK", category: "FLAT", group: "Apartment / Flat" },
  { value: "2_BHK", label: "2 BHK", category: "FLAT", group: "Apartment / Flat" },
  { value: "3_BHK", label: "3 BHK", category: "FLAT", group: "Apartment / Flat" },
  { value: "4_PLUS_BHK", label: "4+ BHK", category: "FLAT", group: "Apartment / Flat" },
  { value: "PENTHOUSE", label: "Penthouse", category: "FLAT", group: "Apartment / Flat" },
  
  // House / Villa
  { value: "INDEPENDENT_HOUSE", label: "Independent House", category: "HOUSE", group: "House / Villa" },
  { value: "VILLA", label: "Villa", category: "VILLA", group: "House / Villa" },
  { value: "BUILDER_FLOOR", label: "Builder Floor", category: "HOUSE", group: "House / Villa" },
  { value: "DUPLEX", label: "Duplex", category: "HOUSE", group: "House / Villa" },
  { value: "FARM_HOUSE", label: "Farm House", category: "HOUSE", group: "House / Villa" },
  
  // Plot / Land
  { value: "RESIDENTIAL_PLOT", label: "Residential Plot", category: "PLOT", group: "Plot / Land" },
  { value: "COMMERCIAL_PLOT", label: "Commercial Plot", category: "PLOT", group: "Plot / Land" },
  { value: "AGRICULTURAL_LAND", label: "Agricultural Land", category: "LAND", group: "Plot / Land" },
  { value: "INDUSTRIAL_PLOT", label: "Industrial Plot", category: "PLOT", group: "Plot / Land" },

  // Commercial
  { value: "OFFICE_SPACE", label: "Office Space", category: "COMMERCIAL", group: "Commercial" },
  { value: "SHOP_SHOWROOM", label: "Shop / Showroom", category: "COMMERCIAL", group: "Commercial" },
  { value: "WAREHOUSE_GODOWN", label: "Warehouse / Godown", category: "COMMERCIAL", group: "Commercial" },
  { value: "INDUSTRIAL_BUILDING", label: "Industrial Building", category: "COMMERCIAL", group: "Commercial" },
  { value: "CO_WORKING", label: "Co-working", category: "COMMERCIAL", group: "Commercial" }
];

export default function AdminUsers({ tab: tabProp, roleFilter: roleFilterProp }) {
  const { user: me } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlTab = searchParams.get("tab");

  const activeTab = useMemo(() => {
    if (tabProp) return tabProp;
    if (pathname.includes("/admin/offices")) return "offices";
    if (pathname.includes("/admin/properties")) return "properties";
    if (pathname.includes("/admin/plots")) return "plots";
    if (pathname.includes("/admin/agents")) return "users";
    return urlTab || "users";
  }, [tabProp, pathname, urlTab]);

  const roleFilter = useMemo(() => {
    if (roleFilterProp) return roleFilterProp;
    if (pathname.includes("/admin/agents")) return "agent";
    return searchParams.get("role");
  }, [roleFilterProp, pathname, searchParams]);

  const handleTabChange = (newTab) => {
    if (newTab === "users") navigate("/dashboard/admin/users");
    else if (newTab === "offices") navigate("/dashboard/admin/offices");
    else if (newTab === "properties") navigate("/dashboard/admin/properties");
    else if (newTab === "plots") navigate("/dashboard/admin/plots-on-demand/manage");
  };

  // Sync queries from URL search param q
  const [users, setUsers] = useState([]);
  const [userQuery, setUserQuery] = useState(searchParams.get("q") || "");
  const [selectedUser, setSelectedUser] = useState(null);
  const [userFormErrors, setUserFormErrors] = useState({});
  const [userConfirmText, setUserConfirmText] = useState("");
  const [userLeads, setUserLeads] = useState([]);
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [blockModalOpen, setBlockModalOpen] = useState(false);
  const [blockModalAction, setBlockModalAction] = useState("block");
  const [blockReason, setBlockReason] = useState("");

  const [offices, setOffices] = useState([]);
  const [officeQuery, setOfficeQuery] = useState(searchParams.get("q") || "");
  const [selectedOffice, setSelectedOffice] = useState(null);
  const [officeFormErrors, setOfficeFormErrors] = useState({});

  const [properties, setProperties] = useState([]);
  const [propPage, setPropPage] = useState(1);
  const [propTotal, setPropTotal] = useState(0);
  const [propQuery, setPropQuery] = useState(searchParams.get("q") || "");
  const [selectedProp, setSelectedProp] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [demands, setDemands] = useState([]);
  const [plotQuery, setPlotQuery] = useState(searchParams.get("q") || "");
  const [selectedDemand, setSelectedDemand] = useState(null);
  const [demandEvents, setDemandEvents] = useState([]);
  const [demandAdminNote, setDemandAdminNote] = useState("");
  const selectedDemandId = selectedDemand?.id || selectedDemand?.demand_id || selectedDemand?.plot_demand_id;

  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [loadingSections, setLoadingSections] = useState({});

  const recordLoadError = useCallback((label, requestError) => {
    setLoadError(`${label}: ${formatApiError(requestError)}`);
  }, []);
  const clearLoadError = useCallback((label) => {
    setLoadError((current) => current.startsWith(`${label}:`) ? "" : current);
  }, []);
  const markLoading = useCallback((label, value) => {
    setLoadingSections((current) => ({ ...current, [label]: value }));
  }, []);

  useEffect(() => {
    const q = searchParams.get("q") || "";
    setUserQuery(q);
    setOfficeQuery(q);
    setPropQuery(q);
    setPlotQuery(q);
  }, [searchParams]);

  useEffect(() => {
    setLoadError("");
  }, [activeTab]);

  const updateQueryParam = (newQ) => {
    const nextParams = new URLSearchParams(searchParams);
    if (newQ) {
      nextParams.set("q", newQ);
    } else {
      nextParams.delete("q");
    }
    setSearchParams(nextParams, { replace: true });
  };

  const selectPropertyWithDetails = useCallback((p) => {
    if (!p) return;
    setSelectedProp(p);
    api.get(`/v1/admin/property-explorer/${encodeURIComponent(p.id || p.property_id)}`)
      .then(({ data }) => {
        const prop = data?.property || data;
        setSelectedProp(prop);
      })
      .catch((requestError) => toast.error(formatApiError(requestError)));
  }, []);

  // LOAD DATA FUNCTIONS
  const loadUsers = useCallback(() => {
    markLoading("Users", true);
    api.get(`/admin/users${userQuery ? `?q=${encodeURIComponent(userQuery)}` : ""}`)
      .then(({ data }) => {
        const items = asItems(data);
        setUsers(items);
        clearLoadError("Users");
      })
      .catch((requestError) => {
        setUsers([]);
        recordLoadError("Users", requestError);
      })
      .finally(() => markLoading("Users", false));
  }, [userQuery, clearLoadError, recordLoadError, markLoading]);

  const loadOffices = useCallback(() => {
    markLoading("Offices", true);
    api.get("/v1/admin/directory/offices", { params: { limit: 200 } })
      .then(({ data }) => {
        setOffices(asItems(data));
        clearLoadError("Offices");
      })
      .catch((requestError) => {
        setOffices([]);
        recordLoadError("Offices", requestError);
      })
      .finally(() => markLoading("Offices", false));
  }, [clearLoadError, recordLoadError, markLoading]);

  const loadProperties = useCallback(() => {
    markLoading("Properties", true);
    let url = `/admin/properties?page=${propPage}&limit=10`;
    if (propQuery) url += `&q=${encodeURIComponent(propQuery)}`;
    api.get(url)
      .then(({ data }) => {
        setProperties(asItems(data));
        setPropTotal(data?.total || 0);
        clearLoadError("Properties");
      })
      .catch((requestError) => {
        setProperties([]);
        setPropTotal(0);
        recordLoadError("Properties", requestError);
      })
      .finally(() => markLoading("Properties", false));
  }, [propPage, propQuery, clearLoadError, recordLoadError, markLoading]);

  const loadPlotDemands = useCallback(() => {
    markLoading("Plots on Demand", true);
    api.get("/admin/plot-demands")
      .then(({ data }) => {
        setDemands(asItems(data));
        clearLoadError("Plots on Demand");
      })
      .catch((requestError) => {
        setDemands([]);
        recordLoadError("Plots on Demand", requestError);
      })
      .finally(() => markLoading("Plots on Demand", false));
  }, [clearLoadError, recordLoadError, markLoading]);

  const selectedUserId = selectedUser?.user_id || selectedUser?.id;
  const selectedUserEmail = selectedUser?.email;
  const selectedUserPhone = selectedUser?.phone;
  const selectedUserRole = selectedUser?.role;

  useEffect(() => {
    if (selectedUser && String(selectedUserRole).toUpperCase() === "BUYER") {
      setLoadingLeads(true);
      api.get("/leads")
        .then(({ data }) => {
          const allLeads = asItems(data?.items || data);
          const filtered = allLeads.filter(l => 
            (l.buyer_user_id && String(l.buyer_user_id) === String(selectedUserId)) ||
            (l.buyer_email && selectedUserEmail && String(l.buyer_email).toLowerCase() === String(selectedUserEmail).toLowerCase()) ||
            (l.buyer_phone && selectedUserPhone && String(l.buyer_phone) === String(selectedUserPhone))
          );
          setUserLeads(filtered);
        })
        .catch((requestError) => {
          setUserLeads([]);
          toast.error(`Buyer leads: ${formatApiError(requestError)}`);
        })
        .finally(() => setLoadingLeads(false));
    } else {
      setUserLeads([]);
    }
  }, [selectedUserId, selectedUserEmail, selectedUserPhone, selectedUserRole]);

  // LOAD INITIAL OFFICES TO ENSURE ASSIGNMENT LIST ALWAYS AVAILABLE
  useEffect(() => {
    loadOffices();
  }, [loadOffices]);

  useEffect(() => {
    if (activeTab === "users") {
      const t = setTimeout(loadUsers, 250);
      return () => clearTimeout(t);
    }
  }, [activeTab, loadUsers]);

  useEffect(() => {
    if (activeTab === "offices") {
      loadOffices();
    }
  }, [activeTab, loadOffices]);

  useEffect(() => {
    if (activeTab === "properties") {
      const t = setTimeout(loadProperties, 250);
      return () => clearTimeout(t);
    }
  }, [activeTab, loadProperties]);

  useEffect(() => {
    if (activeTab === "plots") {
      loadPlotDemands();
      loadOffices();
      loadUsers();
    }
  }, [activeTab, loadPlotDemands, loadOffices, loadUsers]);

  // AUTO-SELECT FIRST ITEMS
  useEffect(() => {
    if (activeTab === "users" && users.length > 0) {
      const selectId = searchParams.get("select");
      if (selectId) {
        if (String(selectedUser?.id || selectedUser?.user_id || "") === selectId) return;
        const matched = users.find(u => String(u.id || u.user_id) === selectId);
        if (matched) {
          setSelectedUser(matched);
          return;
        }
        // The admin list is deliberately capped. Resolve an exact cross-page
        // link by ID when the requested buyer/owner/agent is outside that cap.
        api.get(`/admin/users/${encodeURIComponent(selectId)}`)
          .then(({ data }) => setSelectedUser(data))
          .catch((requestError) => toast.error(formatApiError(requestError)));
        return;
      }
      const filtered = roleFilter === "agent" 
        ? users.filter(u => String(u.role || "").toLowerCase() === "agent")
        : users.filter(u => { const r = String(u.role || "").toLowerCase(); return r === "buyer" || r === "owner"; });
      if (filtered.length > 0) {
        if (!selectedUser || !filtered.some(f => (f.id || f.user_id) === (selectedUser.id || selectedUser.user_id))) {
          setSelectedUser(filtered[0]);
        }
      } else {
        setSelectedUser(null);
      }
    }
  }, [activeTab, users, roleFilter, selectedUser, searchParams]);

  useEffect(() => {
    if (activeTab === "offices" && offices.length > 0) {
      const selectId = searchParams.get("select");
      if (selectId) {
        if (String(selectedOffice?.office_id || selectedOffice?.id || "") === selectId) return;
        const matched = offices.find(o => String(o.office_id || o.id) === selectId);
        if (matched) {
          setSelectedOffice(matched);
          return;
        }
        api.get(`/v1/admin/directory/offices/${encodeURIComponent(selectId)}`)
          .then(({ data }) => setSelectedOffice(data?.office || data))
          .catch((requestError) => toast.error(formatApiError(requestError)));
        return;
      }
      if (!selectedOffice) {
        setSelectedOffice(offices[0]);
      }
    }
  }, [activeTab, offices, selectedOffice, searchParams]);

  useEffect(() => {
    if (activeTab === "properties" && properties.length > 0) {
      const selectId = searchParams.get("select");
      if (selectId) {
        const matched = properties.find(p => String(p.id || p.property_id) === selectId);
        if (matched) {
          selectPropertyWithDetails(matched);
          return;
        }
        // Lead Explorer links may target a property outside this page's
        // ten-row result window. Fetch that exact authorized record so the
        // deep link always opens the requested property instead of silently
        // selecting the first visible row.
        if (String(selectedProp?.id || selectedProp?.property_id || "") !== selectId) {
          api.get(`/v1/admin/property-explorer/${encodeURIComponent(selectId)}`)
            .then(({ data }) => setSelectedProp(data?.property || data))
            .catch((requestError) => toast.error(formatApiError(requestError)));
          return;
        }
      }
      if (!selectedProp) {
        selectPropertyWithDetails(properties[0]);
      }
    }
  }, [activeTab, properties, selectedProp, selectPropertyWithDetails, searchParams]);

  useEffect(() => {
    if (activeTab === "plots" && demands.length > 0) {
      const selectId = searchParams.get("select");
      if (selectId) {
        if (String(selectedDemand?.id || selectedDemand?.demand_id || selectedDemand?.plot_demand_id || "") === selectId) return;
        const matched = demands.find(d => String(d.id || d.lead_id) === selectId);
        if (matched) {
          setSelectedDemand(matched);
          return;
        }
        api.get(`/admin/plot-demands/${encodeURIComponent(selectId)}`)
          .then(({ data }) => setSelectedDemand(data))
          .catch((requestError) => toast.error(formatApiError(requestError)));
        return;
      }
      if (!selectedDemand) {
        setSelectedDemand(demands[0]);
      }
    }
  }, [activeTab, demands, selectedDemand, searchParams]);

  // LOAD CONVERSATION HISTORY LOGS FOR PLOTS
  useEffect(() => {
    // Key this effect to the stable record ID. Form edits replace the selected
    // object, but must not refetch history or clear an in-progress admin note.
    setDemandAdminNote("");
    if (selectedDemandId) {
      setDemandEvents([]);
      api.get(`/leads/${encodeURIComponent(selectedDemandId)}/events`)
        .then(({ data }) => setDemandEvents(asItems(data)))
        .catch((requestError) => {
          setDemandEvents([]);
          toast.error(`Plots on Demand history: ${formatApiError(requestError)}`);
        });
    } else {
      setDemandEvents([]);
    }
  }, [selectedDemandId]);

  // USER CRUD OPERATIONS
  const handleUserSave = async (e) => {
    e.preventDefault();
    if (!selectedUser) return;
    const isExistingUser = Boolean(selectedUser.id || selectedUser.user_id);
    const nextErrors = {};

    if (!String(selectedUser.name || "").trim()) {
      nextErrors.name = "Display name is required.";
    }
    if (!String(selectedUser.email || "").trim()) {
      nextErrors.email = "Email address is required.";
    }

    if (Object.keys(nextErrors).length > 0) {
      setUserFormErrors(nextErrors);
      toast.error("Please correct the highlighted required fields.");
      return;
    }
    setUserFormErrors({});

    setBusy(true);
    try {
      const uid = selectedUser.user_id || selectedUser.id;
      if (uid) {
        // RBAC: admins may only change administrative fields. Personal details
        // (name, email, phone) stay under the user's own control and are never
        // sent from here — the backend ignores them regardless.
        await api.put(`/v1/admin/users/${uid}`, {
          role: selectedUser.role,
          status: selectedUser.status,
          office_id: selectedUser.office_id || "",
        });
        toast.success("User account updated. The user has been notified.");
      } else {
        const { data } = await api.post("/admin/users", {
          name: selectedUser.name,
          email: selectedUser.email,
          phone: selectedUser.phone,
          role: selectedUser.role,
          office_id: selectedUser.office_id || "",
        });
        toast.success(data?.created
          ? `Account created for ${selectedUser.name}. They must use Forgot Password to set their password.`
          : `Existing account for ${selectedUser.name} was updated.`);
      }
      if (!isExistingUser) {
        setSelectedUser(null);
      }
      loadUsers();
    } catch (err) {
      const userMessage = err?.response?.data?.user_message;
      if (userRole === "AGENT" && userMessage) {
        setUserFormErrors((current) => ({ ...current, office_id: userMessage }));
      }
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const handleUserBlockAction = async () => {
    if (!selectedUser) return;
    setBusy(true);
    try {
      const uid = selectedUser.user_id || selectedUser.id;
      if (blockModalAction === "unblock") {
        await api.post(`/v1/admin/users/${uid}/unblock`);
        toast.success(`Unblocked account ${selectedUser.email || selectedUser.name}`);
        setSelectedUser(prev => prev ? { ...prev, status: "ACTIVE" } : null);
      } else {
        await api.delete(`/admin/users/${uid}`, { data: { reason: blockReason || undefined } });
        toast.success(`Blocked account ${selectedUser.email || selectedUser.name}. The user has been notified.`);
        setSelectedUser(prev => prev ? { ...prev, status: "BLOCKED" } : null);
      }
      loadUsers();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
      setBlockModalOpen(false);
      setBlockReason("");
    }
  };

  // OFFICE CRUD OPERATIONS
  const handleOfficeSave = async (e) => {
    e.preventDefault();
    if (!selectedOffice) return;
    const nextErrors = {};
    if (!String(selectedOffice.name || selectedOffice.office_name || "").trim()) {
      nextErrors.name = "Office name is required.";
    }
    if (!String(selectedOffice.city || "").trim()) {
      nextErrors.city = "City is required.";
    }
    if (!String(selectedOffice.phone || "").trim()) {
      nextErrors.phone = "Contact phone is required.";
    }
    if (!String(selectedOffice.email || "").trim()) {
      nextErrors.email = "Email address is required.";
    }
    if (Object.keys(nextErrors).length > 0) {
      setOfficeFormErrors(nextErrors);
      toast.error("Please correct the highlighted required fields.");
      return;
    }
    setOfficeFormErrors({});
    setBusy(true);
    try {
      if (selectedOffice.id || selectedOffice.office_id) {
        const id = selectedOffice.id || selectedOffice.office_id;
        await api.put(`/v1/admin/offices/${id}`, selectedOffice);
        toast.success("Office updated successfully");
      } else {
        await api.post("/v1/admin/offices", selectedOffice);
        toast.success("Office created successfully");
      }
      loadOffices();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const handleOfficeDelete = async () => {
    if (!selectedOffice) return;
    setBusy(true);
    try {
      const id = selectedOffice.id || selectedOffice.office_id;
      await api.delete(`/v1/admin/offices/${id}`);
      toast.success("Office deleted successfully");
      setDeleteTarget(null);
      setSelectedOffice(null);
      loadOffices();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  // PROPERTY CRUD OPERATIONS
  const handlePropSave = async (e) => {
    e.preventDefault();
    if (!selectedProp) return;
    setBusy(true);
    try {
      const id = selectedProp.id || selectedProp.property_id;
      await api.put(`/v1/properties/${id}`, selectedProp);
      const trending = selectedProp.trending === true || selectedProp.raw?.trending === true;
      // Visibility flags use the dedicated audited admin endpoint; catalog edits
      // cannot silently bypass the property lifecycle or moderation history.
      if (String(selectedProp.status || "").toUpperCase() === "PUBLISHED" || !trending) {
        await api.patch(`/admin/properties/${id}/feature`, { trending });
      }
      toast.success("Property details updated successfully");
      loadProperties();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const handlePropDelete = async () => {
    if (!selectedProp) return;
    const status = String(selectedProp.status || "").toUpperCase();
    if (!["DRAFT", "REJECTED", "ARCHIVED"].includes(status)) {
      toast.error("Only unused draft, rejected, or archived listings can be permanently deleted.");
      return;
    }
    setBusy(true);
    try {
      const id = selectedProp.id || selectedProp.property_id;
      await api.delete(`/v1/admin/properties/${id}`);
      toast.success("Property permanently deleted");
      setDeleteTarget(null);
      setSelectedProp(null);
      setPropPage(1);
      loadProperties();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  // PLOT PLOTS/DEMANDS CRUD
  const handleDemandSave = async (e) => {
    e.preventDefault();
    if (!selectedDemand) return;
    if (String(selectedDemand.status || "").toUpperCase() === "CANCELLED" && !demandAdminNote.trim()) {
      toast.error("Add a cancellation reason before saving.");
      return;
    }
    setBusy(true);
    try {
      const id = selectedDemand.id || selectedDemand.demand_id || selectedDemand.plot_demand_id;
      await api.put(`/admin/plot-demands/${id}/status`, {
        status: selectedDemand.status,
        assigned_agent_id: selectedDemand.assigned_agent_id || null,
        assigned_office_id: selectedDemand.assigned_office_id || null,
        note: demandAdminNote.trim() || null,
      });
      toast.success("Plots on Demand request updated successfully");
      setDemandAdminNote("");
      loadPlotDemands();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  // FILTERED LIST COMPUTATIONS
  const userCounts = useMemo(() => {
    const map = { admin: 0, office: 0, agent: 0, buyer: 0, owner: 0 };
    users.forEach(u => {
      let r = String(u.role || "").toLowerCase();
      if (r === "office_user") r = "office";
      if (map[r] != null) map[r]++;
    });
    return map;
  }, [users]);

  const displayedUsers = useMemo(() => {
    if (roleFilter === "agent") {
      return users.filter(u => String(u.role || "").toLowerCase() === "agent");
    }
    return users.filter(u => {
      const r = String(u.role || "").toLowerCase();
      return r === "buyer" || r === "owner";
    });
  }, [users, roleFilter]);

  const filteredOffices = useMemo(() => {
    if (!officeQuery) return offices;
    const q = officeQuery.toLowerCase().trim();
    return offices.filter(o => 
      String(o.name || o.office_name || "").toLowerCase().includes(q) ||
      String(o.city || "").toLowerCase().includes(q) ||
      String(o.locality || "").toLowerCase().includes(q)
    );
  }, [offices, officeQuery]);

  const filteredPlots = useMemo(() => {
    if (!plotQuery) return demands;
    const q = plotQuery.toLowerCase().trim();
    return demands.filter(d => 
      String(d.buyer_name || "").toLowerCase().includes(q) ||
      String(d.contact_phone || "").toLowerCase().includes(q) ||
      String(d.location || "").toLowerCase().includes(q) ||
      String(d.buyer_user_id || "").toLowerCase().includes(q)
    );
  }, [demands, plotQuery]);

  const headerInfo = useMemo(() => {
    switch (activeTab) {
      case "users":
        if (roleFilter === "agent") {
          return { title: "Agent Accounts", subtitle: "Assign agents to regional offices and manage profile configurations." };
        }
        return { title: "User Profiles", subtitle: "Manage system-registered buyers and owners." };
      case "offices":
        return { title: "Branch Office Control", subtitle: "Manage regional branch offices and operational details." };
      case "properties":
        return { title: "Listing Catalog", subtitle: "Review, verify, reject, or publish cataloged properties." };
      case "plots":
        return { title: "Plots on Demand", subtitle: "Review buyer sourcing requirements, assignments, and activity history." };
      default:
        return { title: "Control Panel", subtitle: "Platform operations." };
    }
  }, [activeTab, roleFilter]);

  const hasSelection = useMemo(() => {
    return !!(
      (activeTab === "users" && selectedUser) ||
      (activeTab === "offices" && selectedOffice) ||
      (activeTab === "properties" && selectedProp) ||
      (activeTab === "plots" && selectedDemand)
    );
  }, [activeTab, selectedUser, selectedOffice, selectedProp, selectedDemand]);

  const retryActiveList = () => {
    if (activeTab === "users") {
      loadUsers();
      loadOffices();
    } else if (activeTab === "offices") {
      loadOffices();
    } else if (activeTab === "properties") {
      loadProperties();
      loadOffices();
    } else {
      loadPlotDemands();
      loadOffices();
      loadUsers();
    }
  };

  const activeListLoading = activeTab === "users"
    ? Boolean(loadingSections.Users || loadingSections.Offices)
    : activeTab === "offices"
      ? Boolean(loadingSections.Offices)
      : activeTab === "properties"
        ? Boolean(loadingSections.Properties || loadingSections.Offices)
        : Boolean(loadingSections["Plots on Demand"] || loadingSections.Offices || loadingSections.Users);

  return (
    <div className="space-y-6 flex flex-col h-full min-h-0 bg-background text-foreground" data-testid="admin-users-page">
      {/* Header Info */}
      <header className="shrink-0 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-muted-foreground mb-1.5">Control Plane · Admin Portal</p>
          <h1 className="font-display text-3xl font-black tracking-tight text-primary uppercase"><span className="brand-gradient-text">{headerInfo.title}</span></h1>
          <p className="text-xs text-muted-foreground">{headerInfo.subtitle}</p>
        </div>
      </header>



      {/* Grid Split Pane Layout */}
      <div className="flex-1 grid grid-cols-1 xl:grid-cols-12 gap-6 min-h-0 animate-fade-up">
        
        {/* Left Column: List Pane */}
        <div className={`xl:col-span-5 flex flex-col gap-4 bg-card border border-border/40 rounded-2xl p-4 xl:h-[calc(100vh-160px)] xl:overflow-y-auto shadow-sm ${hasSelection ? "hidden xl:flex" : "flex"}`}>
          
          {/* Sub-header Search Strip */}
          <div className="shrink-0 flex items-center justify-between gap-4">
            <h3 className="font-display font-black text-sm uppercase tracking-wider text-muted-foreground">List Selection</h3>
            
            {activeTab === "users" && (
              <Button
                size="sm"
                onClick={() => setSelectedUser({
                  name: "",
                  email: "",
                  phone: "",
                  role: String(roleFilter || "AGENT").toUpperCase(),
                  status: "PENDING",
                  office_id: "",
                })}
                className="gap-1 font-bold text-xs"
                title="Pre-provision a user who will set their own password"
                data-testid="create-user-btn"
              >
                <Plus className="w-3.5 h-3.5" /> Add User
              </Button>
            )}
            {activeTab === "offices" && (
              <Button
                size="sm"
                onClick={() => setSelectedOffice({ name: "", city: "", locality: "", phone: "", email: "" })}
                className="gap-1 font-bold text-xs"
                title="Create a new office"
                data-testid="create-office-btn"
              >
                <Plus className="w-3.5 h-3.5" /> Add Office
              </Button>
            )}
          </div>

          {/* Search Inputs */}
          <div className="shrink-0 relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            {activeTab === "users" && (
              <Input
                value={userQuery}
                onChange={(e) => { setUserQuery(e.target.value); updateQueryParam(e.target.value); }}
                placeholder="Search name, email, phone..."
                className="pl-9 bg-card text-xs"
                data-testid="users-search"
              />
            )}
            {activeTab === "offices" && (
              <Input
                value={officeQuery}
                onChange={(e) => { setOfficeQuery(e.target.value); updateQueryParam(e.target.value); }}
                placeholder="Search office name, city..."
                className="pl-9 bg-card text-xs"
              />
            )}
            {activeTab === "properties" && (
              <Input
                value={propQuery}
                onChange={(e) => { setPropQuery(e.target.value); setPropPage(1); updateQueryParam(e.target.value); }}
                placeholder="Search code, title, location..."
                className="pl-9 bg-card text-xs"
                data-testid="properties-search"
              />
            )}
            {activeTab === "plots" && (
              <Input
                value={plotQuery}
                onChange={(e) => { setPlotQuery(e.target.value); updateQueryParam(e.target.value); }}
                placeholder="Search buyer name, phone..."
                className="pl-9 bg-card text-xs"
              />
            )}
          </div>

          {/* Render List Items */}
          <div className="flex-grow space-y-2 overflow-y-auto pr-1">
            {activeListLoading && <div className="space-y-2" aria-label="Loading records">{[0, 1, 2, 3].map((item) => <div key={item} className="h-20 animate-pulse rounded-xl bg-muted"/>)}</div>}
            {!activeListLoading && loadError && <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-4 text-center" role="alert"><p className="text-xs font-bold">Records could not be loaded.</p><p className="mt-1 text-[11px] text-muted-foreground">{loadError}</p><Button type="button" size="sm" variant="outline" className="mt-3 gap-2" onClick={retryActiveList}><RefreshCw className="h-3.5 w-3.5" aria-hidden="true"/>Retry</Button></div>}
            
            {/* Users Tab Cards */}
            {!activeListLoading && !loadError && activeTab === "users" && displayedUsers.map(u => {
              const mappedOffice = offices.find(o => (o.id || o.office_id) === u.office_id);
              const isSelected = selectedUser && (selectedUser.user_id || selectedUser.id) === (u.user_id || u.id);
              return (
                <button
                  type="button"
                  key={u.user_id || u.id}
                  aria-pressed={isSelected}
                  onClick={() => setSelectedUser(u)}
                  title={`Open ${u.name || "user"} account details`}
                  className={`w-full p-3.5 text-left rounded-xl border transition-all duration-200 ease-out cursor-pointer text-xs flex flex-col gap-1.5 relative focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
                    isSelected ? "bg-muted border-primary/60 shadow-sm" : "bg-card border-border/80 hover:border-primary/40"
                  }`}
                  data-testid={`user-row-${u.user_id || u.id}`}
                >
                  {isSelected && <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary rounded-l-xl" />}
                  <div className="flex justify-between items-start gap-2">
                    <h4 className="font-bold text-foreground truncate">{u.name}</h4>
                    <span className={`text-[9px] font-mono font-bold tracking-wider px-2 py-0.5 rounded-full capitalize ${ROLE_COLORS[String(u.role || "buyer").toLowerCase()] || ROLE_COLORS.buyer}`}>
                      {u.role}
                    </span>
                  </div>
                  <p className="text-muted-foreground truncate">{u.email}</p>
                  <div className="flex justify-between items-center text-[10px] text-muted-foreground pt-2 border-t border-border/20 mt-1">
                    <span>Office: {mappedOffice ? mappedOffice.name : (u.role === "AGENT" ? "Unassigned ⚠" : "—")}</span>
                    <span className={u.status === "ACTIVE" ? "text-emerald-500 font-bold" : "text-amber-500 font-bold"}>{u.status || "PENDING"}</span>
                  </div>
                </button>
              );
            })}

            {/* Offices Tab Cards */}
            {!activeListLoading && !loadError && activeTab === "offices" && filteredOffices.map(o => {
              const isSelected = selectedOffice && (selectedOffice.office_id || selectedOffice.id) === (o.office_id || o.id);
              return (
                <button
                  type="button"
                  key={o.office_id || o.id}
                  aria-pressed={isSelected}
                  onClick={() => setSelectedOffice(o)}
                  title={`Open ${o.name || o.office_name || "office"} details`}
                  className={`w-full p-3.5 text-left rounded-xl border transition-all duration-200 ease-out cursor-pointer text-xs flex flex-col gap-1.5 relative focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
                    isSelected ? "bg-muted border-primary/60 shadow-sm" : "bg-card border-border/80 hover:border-primary/40"
                  }`}
                  data-testid={`office-row-${o.office_id || o.id}`}
                >
                  {isSelected && <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary rounded-l-xl" />}
                  <h4 className="font-bold text-foreground truncate">{o.name || o.office_name}</h4>
                  <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
                    <MapPin className="w-3 h-3 text-muted-foreground shrink-0" />
                    <span>{o.locality ? `${o.locality}, ` : ""}{o.city}</span>
                  </div>
                  <p className="text-muted-foreground text-[10px] font-mono">{o.phone || "—"}</p>
                </button>
              );
            })}

            {/* Properties Tab Cards */}
            {!activeListLoading && !loadError && activeTab === "properties" && properties.map(p => {
              const isSelected = selectedProp && (selectedProp.property_id || selectedProp.id) === (p.property_id || p.id);
              return (
                <button
                  type="button"
                  key={p.property_id || p.id}
                  style={{ animationDelay: `${(properties.indexOf(p) % 10) * 50}ms`, animationFillMode: 'both' }}
                  aria-pressed={isSelected}
                  onClick={() => selectPropertyWithDetails(p)}
                  title={`Open ${p.title || p.property_code || "property"} details`}
                  className={`w-full p-4 text-left rounded-xl border transition-all duration-200 ease-out cursor-pointer text-xs flex flex-col gap-2 relative animate-fade-up focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
                    isSelected ? "bg-card border-primary shadow-md scale-[1.02] z-10" : "bg-card/50 border-border hover:bg-card hover:border-primary/40 hover:shadow-sm"
                  }`}
                  data-testid={`prop-row-${p.property_id || p.id}`}
                >
                  {isSelected && <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary rounded-l-xl" />}
                  <div className="flex justify-between items-start gap-2">
                    <span className="font-mono text-[9px] text-muted-foreground uppercase">{p.property_code}</span>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {(p.trending === true || p.raw?.trending === true) && (
                        <span className="bg-orange-500/10 text-orange-500 border border-orange-500/20 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full flex items-center gap-0.5 animate-pulse">
                          🔥 Trending
                        </span>
                      )}
                      <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full ${
                        p.status === "PUBLISHED" ? "bg-emerald-100 text-emerald-800" : p.status === "VERIFIED" ? "bg-blue-100 text-blue-800" : p.status === "SOLD" ? "bg-slate-200 text-slate-800" : p.status === "PENDING_SOLD_VERIFICATION" ? "bg-amber-100 text-amber-800" : p.status === "BOOKED" ? "bg-purple-100 text-purple-800" : "bg-amber-100 text-amber-800"
                      }`}>
                        {(p.status || "").replace(/_/g, " ")}
                      </span>
                    </div>
                  </div>
                  <h4 className="font-bold text-foreground truncate">{p.title}</h4>
                  <div className="flex justify-between items-center text-[10px] font-semibold text-emerald-600 mt-2 border-t border-border/20 pt-1.5">
                    <span>{p.city}</span>
                    <span>{p.price != null ? formatPrice(p.price) : "Price unavailable"}</span>
                  </div>
                </button>
              );
            })}

            {/* Plots Tab Cards */}
            {!activeListLoading && !loadError && activeTab === "plots" && filteredPlots.map(d => {
              const isSelected = selectedDemand && selectedDemand.id === d.id;
              return (
                <button
                  type="button"
                  key={d.id}
                  aria-pressed={isSelected}
                  onClick={() => setSelectedDemand(d)}
                  title={`Open Plots on Demand request from ${d.buyer_name || "buyer"}`}
                  className={`w-full p-3.5 text-left rounded-xl border transition-all duration-200 ease-out cursor-pointer text-xs flex flex-col gap-1.5 relative focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
                    isSelected ? "bg-muted border-primary/60 shadow-sm" : "bg-card border-border/80 hover:border-primary/40"
                  }`}
                  data-testid={`demand-row-${d.id}`}
                >
                  {isSelected && <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary rounded-l-xl" />}
                  <div className="flex justify-between items-start gap-2">
                    <h4 className="font-bold text-foreground truncate">{d.buyer_name || "Buyer name unavailable"}</h4>
                    <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${
                      d.status === "COMPLETED" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"
                    }`}>
                      {d.status}
                    </span>
                  </div>
                  <p className="text-muted-foreground truncate">{d.location}</p>
                  <div className="flex justify-between items-center text-[10px] font-semibold text-emerald-600 mt-2 pt-1 border-t border-border/20">
                    <span>Area: {formatArea(d.area, d.area_unit || "sqyd")}</span>
                    <span>Budget: {d.budget != null ? formatPrice(d.budget) : "Unavailable"}</span>
                  </div>
                </button>
              );
            })}

            {!activeListLoading && !loadError && activeTab === "properties" && properties.length > 0 && (
              <div className="pt-2 shrink-0">
                <Pagination page={propPage} limit={10} total={propTotal} onPageChange={setPropPage} />
              </div>
            )}

            {/* Empty States */}
            {!activeListLoading && !loadError && activeTab === "users" && displayedUsers.length === 0 && <p className="text-center py-8 text-xs text-muted-foreground italic">No users found.</p>}
            {!activeListLoading && !loadError && activeTab === "offices" && filteredOffices.length === 0 && <p className="text-center py-8 text-xs text-muted-foreground italic">No offices registered.</p>}
            {!activeListLoading && !loadError && activeTab === "properties" && properties.length === 0 && <p className="text-center py-8 text-xs text-muted-foreground italic">No listings found.</p>}
            {!activeListLoading && !loadError && activeTab === "plots" && filteredPlots.length === 0 && <p className="text-center py-8 text-xs text-muted-foreground italic">No Plots on Demand records found.</p>}
          </div>
        </div>

        {/* Right Column: Detail / Form Editor Pane */}
        <div className={`xl:col-span-7 bg-card border border-border/80 rounded-2xl p-6 shadow-md flex flex-col xl:h-[calc(100vh-160px)] xl:overflow-y-auto ${hasSelection ? "flex" : "hidden xl:flex"}`}>
          
          {/* Mobile Back Button */}
          {hasSelection && (
            <div className="xl:hidden flex items-center mb-4 shrink-0 pb-2 border-b border-border/40">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  if (activeTab === "users") setSelectedUser(null);
                  else if (activeTab === "offices") setSelectedOffice(null);
                  else if (activeTab === "properties") setSelectedProp(null);
                  else if (activeTab === "plots") setSelectedDemand(null);
                }}
                className="gap-1.5 text-primary font-bold hover:bg-muted/50 pl-0"
              >
                <ArrowLeft className="w-4 h-4" /> Back to List
              </Button>
            </div>
          )}
          
          {/* USER TAB DETAILS & FORM */}
          {activeTab === "users" && (
            selectedUser ? (
              <>
                <form onSubmit={handleUserSave} className="space-y-6">
                <div className="flex justify-between items-start border-b border-border/50 pb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-full bg-primary/10 text-primary font-bold flex items-center justify-center text-sm">
                      {selectedUser.name ? selectedUser.name.split(" ").map(n=>n[0]).join("").substring(0,2).toUpperCase() : "?"}
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-foreground">{selectedUser.name}</h2>
                      <p className="text-xs text-muted-foreground">User ID: <span className="font-mono text-[10px]">{selectedUser.user_id || selectedUser.id}</span></p>
                      {["agent", "office", "office_user"].includes(String(selectedUser.role || "").toLowerCase()) && (
                        <Link
                          to={String(selectedUser.role || "").toLowerCase() === "agent"
                            ? `/agents/${selectedUser.user_id || selectedUser.id}`
                            : `/offices/${selectedUser.office_id || selectedUser.user_id || selectedUser.id}`}
                          target="_blank"
                          rel="noreferrer"
                          className="mt-1 inline-flex items-center gap-1 text-[11px] font-bold text-primary hover:underline"
                        >
                          <ExternalLink className="w-3 h-3" /> View public profile
                        </Link>
                      )}
                    </div>
                  </div>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded border ${ROLE_COLORS[String(selectedUser.role || "buyer").toLowerCase()] || ROLE_COLORS.buyer}`}>
                    {selectedUser.role}
                  </span>
                </div>

                <div className="space-y-4">
                  <div className="grid gap-1.5">
                    <Label htmlFor="det-u-name">Display Name <span className="text-destructive">*</span></Label>
                    <Input id="det-u-name" value={selectedUser.name || ""} onChange={(e) => { setSelectedUser({ ...selectedUser, name: e.target.value }); setUserFormErrors((current) => ({ ...current, name: "" })); }} required readOnly={!!(selectedUser.user_id || selectedUser.id)} disabled={!!(selectedUser.user_id || selectedUser.id)} aria-invalid={Boolean(userFormErrors.name)} />
                    {userFormErrors.name && <p className="text-[11px] text-destructive">{userFormErrors.name}</p>}
                  </div>

                  <div className="grid gap-1.5">
                    <Label htmlFor="det-u-email">Email Address <span className="text-destructive">*</span></Label>
                    <Input id="det-u-email" type="email" value={selectedUser.email || ""} onChange={(e) => { setSelectedUser({ ...selectedUser, email: e.target.value }); setUserFormErrors((current) => ({ ...current, email: "" })); }} required readOnly={!!(selectedUser.user_id || selectedUser.id)} disabled={!!(selectedUser.user_id || selectedUser.id)} aria-invalid={Boolean(userFormErrors.email)} />
                    {userFormErrors.email && <p className="text-[11px] text-destructive">{userFormErrors.email}</p>}
                  </div>

                  <div className="grid gap-1.5">
                    <Label htmlFor="det-u-phone">Phone Number</Label>
                    <Input id="det-u-phone" type="tel" value={selectedUser.phone || ""} onChange={(e) => setSelectedUser({ ...selectedUser, phone: e.target.value })} readOnly={!!(selectedUser.user_id || selectedUser.id)} disabled={!!(selectedUser.user_id || selectedUser.id)} />
                    {!!(selectedUser.user_id || selectedUser.id) && (
                      <span className="text-[10px] text-muted-foreground">Personal details (name, email, phone) can only be changed by the account owner.</span>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="grid gap-1.5">
                      <Label htmlFor="det-u-role">System Role <span className="text-destructive">*</span></Label>
                      <select
                        id="det-u-role"
                        value={String(selectedUser.role || "BUYER").toUpperCase()}
                        onChange={(e) => {
                          setSelectedUser({ ...selectedUser, role: e.target.value });
                          setUserFormErrors((current) => ({ ...current, office_id: "" }));
                        }}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                        required
                      >
                        <option value="BUYER">Buyer</option>
                        <option value="AGENT">Agent</option>
                        <option value="OFFICE_USER">Office User</option>
                        <option value="ADMIN">Admin</option>
                      </select>
                    </div>
                    <div className="grid gap-1.5">
                      <Label htmlFor="det-u-status">Account Status</Label>
                      <select
                        id="det-u-status"
                        value={String(selectedUser.status || "ACTIVE").toUpperCase()}
                        onChange={(e) => setSelectedUser({ ...selectedUser, status: e.target.value })}
                        disabled={!(selectedUser.id || selectedUser.user_id)}
                        title={selectedUser.id || selectedUser.user_id ? "Change account status" : "New accounts remain pending until secure password setup"}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      >
                        <option value="ACTIVE">Active</option>
                        <option value="PENDING">Pending</option>
                        <option value="BLOCKED">Blocked</option>
                      </select>
                      {!(selectedUser.id || selectedUser.user_id) && (
                        <span className="text-[10px] text-muted-foreground">The account activates after the user completes email OTP password setup.</span>
                      )}
                    </div>
                  </div>

                  {["AGENT", "OFFICE", "OFFICE_USER"].includes(String(selectedUser.role || "").toUpperCase()) && (
                    <div className="grid gap-1.5 p-3.5 bg-muted/30 rounded-xl border border-border/40 space-y-1">
                      <Label htmlFor="det-u-office" className="font-bold flex items-center gap-1.5 text-primary">
                        <OfficeIcon className="w-4 h-4 text-muted-foreground"/> Office Assignment <span className="text-destructive font-bold" title="Mandatory for Agents & Office Users">*</span>
                      </Label>
                      <select
                        id="det-u-office"
                        value={selectedUser.office_id || ""}
                        onChange={(e) => {
                          setSelectedUser({ ...selectedUser, office_id: e.target.value });
                          setUserFormErrors((current) => ({ ...current, office_id: "" }));
                        }}
                        className={`flex h-10 w-full rounded-md bg-background px-3 py-2 text-sm mt-1 ${userFormErrors.office_id ? "border-destructive focus-visible:ring-destructive/30" : "border-input"}`}
                        required
                        aria-invalid={Boolean(userFormErrors.office_id)}
                      >
                        <option value="">-- Select Respective Office (Required) --</option>
                        {offices.map(o => (
                          <option key={o.office_id || o.id} value={o.office_id || o.id}>{o.name || o.office_name}</option>
                        ))}
                      </select>
                      {userFormErrors.office_id && <p className="text-[11px] text-destructive">{userFormErrors.office_id}</p>}
                      <span className="text-[10px] text-muted-foreground">Agents and office users must belong to a branch office.</span>
                    </div>
                  )}
                </div>

                <div className="flex justify-between items-center border-t border-border/50 pt-4 mt-6">
                  {(selectedUser.id || selectedUser.user_id) && selectedUser.id !== me?.id && selectedUser.user_id !== me?.user_id ? (
                    selectedUser.status === "BLOCKED" ? (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setBlockModalAction("unblock");
                          setBlockModalOpen(true);
                        }}
                        className="text-emerald-500 hover:bg-emerald-500/10 border-emerald-500/40 font-semibold"
                      >
                        Unblock Account
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setBlockModalAction("block");
                          setBlockModalOpen(true);
                        }}
                        className="text-destructive hover:bg-destructive/10 border-destructive/40 font-semibold"
                      >
                        Block Account
                      </Button>
                    )
                  ) : <div />}

                  <Button type="submit" disabled={busy} className="font-bold" title={selectedUser.id || selectedUser.user_id ? "Save administrative account controls" : "Create account and send setup instructions"}>
                    {busy ? "Saving..." : (selectedUser.id || selectedUser.user_id ? "Save User Changes" : "Create User")}
                  </Button>
                </div>
              </form>

              {/* Related Leads (Only for Buyers) */}
              {selectedUser && String(selectedUser.role || "").toUpperCase() === "BUYER" && (
                <div className="mt-8 pt-6 border-t border-border space-y-4">
                  <h3 className="text-sm font-bold uppercase tracking-wider text-primary flex items-center gap-2">
                    <ClipboardCheck className="w-4 h-4 text-muted-foreground" /> Related Leads & Demands
                  </h3>
                  {loadingLeads ? (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="w-3.5 h-3.5 animate-spin"/> Loading leads…</div>
                  ) : userLeads.length === 0 ? (
                    <p className="text-xs text-muted-foreground italic">No leads or Plots on Demand registered for this buyer.</p>
                  ) : (
                    <div className="space-y-2.5">
                      {userLeads.map((lead) => (
                        <div key={lead.id} className="p-4 bg-muted/20 border border-border/60 rounded-xl space-y-1.5 text-xs">
                          <div className="flex justify-between items-center">
                            <span className="font-mono text-[9px] text-muted-foreground uppercase font-bold tracking-wider">
                              {lead.is_plot_demand ? "Plots on Demand" : "Property Lead"}
                            </span>
                            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded uppercase ${
                              lead.status === "VERIFIED" || lead.status === "CONVERTED" || lead.status === "APPROVED"
                                ? "bg-emerald-500/10 text-emerald-600 border border-emerald-500/20"
                                : lead.status === "REJECTED"
                                ? "bg-red-500/10 text-red-600 border border-red-500/20"
                                : "bg-amber-500/10 text-amber-600 border border-amber-500/20"
                            }`}>
                              {lead.status || "NEW"}
                            </span>
                          </div>
                          <div className="font-bold text-foreground">
                            {lead.is_plot_demand ? (
                              <Link 
                                to={`/dashboard/admin/plots-on-demand/workbench?select=${lead.plot_demand_id || lead.plot_request_id || lead.id}`}
                                title="Click to view Plots on Demand request"
                                className="text-primary hover:underline font-bold"
                              >
                                Plots on Demand Request
                              </Link>
                            ) : (
                              lead.property_id && lead.property_id !== "null" ? (
                                <Link 
                                  to={`/dashboard/admin/properties?select=${lead.property_id}`}
                                  title="Click to view Property details"
                                  className="text-primary hover:underline font-bold"
                                >
                                  Property: {lead.property_title || lead.property_code || `ID: ${lead.property_id} (Details Unavailable)`}
                                </Link>
                              ) : (
                                <span className="text-muted-foreground italic">No property associated (Reference Null)</span>
                              )
                            )}
                          </div>
                          <div className="flex gap-4 text-[10px] text-muted-foreground">
                            <Link 
                              to={`/dashboard/leads?select=${lead.id}`}
                              title="Click to view lead in CRM pipeline"
                              className="text-blue-400 hover:text-blue-300 hover:underline font-semibold"
                            >
                              View CRM Lead
                            </Link>
                            {lead.assigned_to_name && (
                              <div>
                                Agent:{" "}
                                <button
                                  type="button"
                                  onClick={() => {
                                    navigate(`/dashboard/admin/users?select=${lead.assigned_to_user_id || lead.assigned_agent_id}`);
                                  }}
                                  title="Click to view agent profile"
                                  className="font-semibold text-foreground hover:underline hover:text-primary cursor-pointer"
                                >
                                  {lead.assigned_to_name}
                                </button>
                              </div>
                            )}
                          </div>
                          <div className="text-[10px] text-muted-foreground flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5 text-muted-foreground" />
                            <span>Created: {lead.created_at ? new Date(lead.created_at).toLocaleDateString("en-IN") : "—"}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
              <div className="text-center py-20 text-muted-foreground flex flex-col items-center gap-2">
                <UsersIcon className="w-10 h-10 text-muted-foreground/60" />
                <p>Select a user/agent from the list to view profile and update office assignments.</p>
              </div>
            )
          )}

          {/* OFFICE TAB DETAILS & FORM */}
          {activeTab === "offices" && (
            selectedOffice ? (
              <form onSubmit={handleOfficeSave} className="space-y-6">
                <div className="border-b border-border/50 pb-4 flex justify-between items-center">
                  <div>
                    <h2 className="text-lg font-bold text-foreground">
                      {selectedOffice.id || selectedOffice.office_id ? "Office Settings" : "Create New Branch"}
                    </h2>
                    <p className="text-xs text-muted-foreground">Branch operations details and localization mapping.</p>
                  </div>
                  {(selectedOffice.id || selectedOffice.office_id) && (
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => setDeleteTarget({ kind: "office", label: selectedOffice.name || selectedOffice.office_name })}
                      className="text-destructive hover:bg-destructive/10"
                    >
                      <Trash2 className="w-4 h-4 mr-1" /> Delete
                    </Button>
                  )}
                </div>

                <div className="space-y-4">
                  <div className="grid gap-1.5">
                    <Label htmlFor="det-o-name">Office Name <span className="text-destructive">*</span></Label>
                    <Input id="det-o-name" value={selectedOffice.name || selectedOffice.office_name || ""} onChange={(e) => { setSelectedOffice({ ...selectedOffice, name: e.target.value }); setOfficeFormErrors((current) => ({ ...current, name: "" })); }} required aria-invalid={Boolean(officeFormErrors.name)} />
                    {officeFormErrors.name && <p className="text-[11px] text-destructive">{officeFormErrors.name}</p>}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="grid gap-1.5">
                      <Label htmlFor="det-o-city">City <span className="text-destructive">*</span></Label>
                      <Input id="det-o-city" value={selectedOffice.city || ""} onChange={(e) => { setSelectedOffice({ ...selectedOffice, city: e.target.value }); setOfficeFormErrors((current) => ({ ...current, city: "" })); }} required aria-invalid={Boolean(officeFormErrors.city)} />
                      {officeFormErrors.city && <p className="text-[11px] text-destructive">{officeFormErrors.city}</p>}
                    </div>
                    <div className="grid gap-1.5">
                      <Label htmlFor="det-o-locality">Locality</Label>
                      <Input id="det-o-locality" value={selectedOffice.locality || ""} onChange={(e) => setSelectedOffice({ ...selectedOffice, locality: e.target.value })} />
                    </div>
                  </div>

                  <div className="grid gap-1.5">
                    <Label htmlFor="det-o-phone">Contact Phone <span className="text-destructive">*</span></Label>
                    <Input id="det-o-phone" type="tel" value={selectedOffice.phone || ""} onChange={(e) => { setSelectedOffice({ ...selectedOffice, phone: e.target.value }); setOfficeFormErrors((current) => ({ ...current, phone: "" })); }} required aria-invalid={Boolean(officeFormErrors.phone)} />
                    {officeFormErrors.phone && <p className="text-[11px] text-destructive">{officeFormErrors.phone}</p>}
                  </div>

                  <div className="grid gap-1.5">
                    <Label htmlFor="det-o-email">Email Address <span className="text-destructive">*</span></Label>
                    <Input id="det-o-email" type="email" value={selectedOffice.email || ""} onChange={(e) => { setSelectedOffice({ ...selectedOffice, email: e.target.value }); setOfficeFormErrors((current) => ({ ...current, email: "" })); }} required aria-invalid={Boolean(officeFormErrors.email)} />
                    {officeFormErrors.email && <p className="text-[11px] text-destructive">{officeFormErrors.email}</p>}
                  </div>
                </div>

                <div className="flex justify-end border-t border-border/50 pt-4 mt-6">
                  <Button type="submit" disabled={busy} className="font-bold">
                    {busy ? "Saving..." : (selectedOffice.id || selectedOffice.office_id) ? "Update Office" : "Create Branch"}
                  </Button>
                </div>
              </form>
            ) : (
              <div className="text-center py-20 text-muted-foreground flex flex-col items-center gap-2">
                <Building2 className="w-10 h-10 text-muted-foreground/60" />
                <p>Select a branch office to inspect location details or create a new branch office.</p>
              </div>
            )
          )}

          {/* PROPERTIES TAB DETAILS & FORM */}
          {activeTab === "properties" && (
            selectedProp ? (
              <form onSubmit={handlePropSave} className="space-y-6">
                <div className="border-b border-border/50 pb-4 flex justify-between items-start">
                  <div>
                    <span className="font-mono text-xs text-muted-foreground uppercase">{selectedProp.property_code || "Code Pending"}</span>
                    <h2 className="text-lg font-bold text-foreground mt-1">{selectedProp.title}</h2>
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-1">
                      <MapPin className="w-3.5 h-3.5"/>
                      <span>{selectedProp.locality ? `${selectedProp.locality}, ` : ""}{selectedProp.city}</span>
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setDeleteTarget({ kind: "property", label: selectedProp.title })}
                    disabled={!['DRAFT', 'REJECTED', 'ARCHIVED'].includes(String(selectedProp.status || '').toUpperCase()) || busy}
                    title="Permanent deletion is limited to unused draft, rejected, or archived listings"
                    className="text-destructive hover:bg-destructive/10"
                  >
                    <Trash2 className="w-4 h-4 mr-1" /> Purge Listing
                  </Button>
                </div>

                <div className="space-y-4">
                  <div className="grid gap-1.5">
                    <Label htmlFor="det-p-title">Listing Name</Label>
                    <Input id="det-p-title" value={selectedProp.title || ""} onChange={(e) => setSelectedProp({ ...selectedProp, title: e.target.value })} required />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="grid gap-1.5">
                      <Label htmlFor="det-p-category">Listing Category</Label>
                      <select
                        id="det-p-category"
                        value={String(selectedProp.category || "LAND").toUpperCase()}
                        onChange={(e) => {
                          const cat = e.target.value;
                          const firstSub = SUBTYPES.find(s => s.category === cat);
                          setSelectedProp({
                            ...selectedProp,
                            category: cat,
                            subcategory: firstSub ? firstSub.value : ""
                          });
                        }}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      >
                        <option value="LAND">Land Plot</option>
                        <option value="PLOT">Plot</option>
                        <option value="FLAT">Flat / Apartment</option>
                        <option value="HOUSE">House / Villa</option>
                        <option value="COMMERCIAL">Commercial Office</option>
                      </select>
                    </div>

                    <div className="grid gap-1.5">
                      <Label htmlFor="det-p-subcategory">Property Subtype</Label>
                      <select
                        id="det-p-subcategory"
                        value={String(selectedProp.subcategory || "").toUpperCase()}
                        onChange={(e) => {
                          const val = e.target.value;
                          const sel = SUBTYPES.find(s => s.value === val);
                          setSelectedProp({
                            ...selectedProp,
                            subcategory: val,
                            category: sel ? sel.category : selectedProp.category
                          });
                        }}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      >
                        <option value="">Select Subtype</option>
                        <optgroup label="Apartment / Flat">
                          {SUBTYPES.filter(s => s.group === "Apartment / Flat").map(s => (
                            <option key={s.value} value={s.value}>{s.label}</option>
                          ))}
                        </optgroup>
                        <optgroup label="House / Villa">
                          {SUBTYPES.filter(s => s.group === "House / Villa").map(s => (
                            <option key={s.value} value={s.value}>{s.label}</option>
                          ))}
                        </optgroup>
                        <optgroup label="Plot / Land">
                          {SUBTYPES.filter(s => s.group === "Plot / Land").map(s => (
                            <option key={s.value} value={s.value}>{s.label}</option>
                          ))}
                        </optgroup>
                        <optgroup label="Commercial">
                          {SUBTYPES.filter(s => s.group === "Commercial").map(s => (
                            <option key={s.value} value={s.value}>{s.label}</option>
                          ))}
                        </optgroup>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="grid gap-1.5">
                      <Label htmlFor="det-p-type">Transaction Type</Label>
                      <select
                        id="det-p-type"
                        value={String(selectedProp.listing_type || "SELL").toUpperCase()}
                        onChange={(e) => setSelectedProp({ ...selectedProp, listing_type: e.target.value })}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      >
                        <option value="SELL">For Sale</option>
                        <option value="RENT">For Rent</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="grid gap-1.5">
                      <Label htmlFor="det-p-price">Price / Valuation (₹)</Label>
                      <Input id="det-p-price" type="number" min="0" value={selectedProp.price || 0} onChange={(e) => setSelectedProp({ ...selectedProp, price: Math.max(0, parseFloat(e.target.value) || 0) })} required />
                    </div>
                    <div className="grid gap-1.5">
                      <Label htmlFor="det-p-status">Moderation Status</Label>
                      <Input
                        id="det-p-status"
                        value={String(selectedProp.status || "DRAFT").replaceAll("_", " ")}
                        readOnly
                        aria-describedby="det-p-status-help"
                        className="uppercase"
                      />
                      <p id="det-p-status-help" className="text-[11px] text-muted-foreground">
                        Use Property Explorer lifecycle controls for audited status changes.
                      </p>
                    </div>
                  </div>

                  {/* Custom Premium Switch for Trending Toggle */}
                  <div className="flex items-center space-x-3 pt-3 pb-1 border-t border-border/30 mt-2">
                    <button
                      type="button"
                      role="switch"
                      aria-checked={selectedProp.trending === true || selectedProp.raw?.trending === true}
                      disabled={String(selectedProp.status || "").toUpperCase() !== "PUBLISHED"}
                      aria-describedby="trending-help"
                      onClick={() => {
                        const nextVal = !(selectedProp.trending === true || selectedProp.raw?.trending === true);
                        setSelectedProp({
                          ...selectedProp,
                          trending: nextVal,
                          raw: {
                            ...(selectedProp.raw || {}),
                            trending: nextVal
                          }
                        });
                      }}
                      className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${
                        (selectedProp.trending === true || selectedProp.raw?.trending === true) ? "bg-primary" : "bg-muted"
                      }`}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-background shadow ring-0 transition duration-200 ease-in-out ${
                          (selectedProp.trending === true || selectedProp.raw?.trending === true) ? "translate-x-5" : "translate-x-0"
                        }`}
                      />
                    </button>
                    <div className="flex flex-col">
                      <span className="text-sm font-semibold text-foreground">
                        Featured & Trending Leaderboard
                      </span>
                      <span id="trending-help" className="text-[10px] text-muted-foreground">
                        {String(selectedProp.status || "").toUpperCase() === "PUBLISHED"
                          ? "Show this published property in the trending catalogue."
                          : "Publish this property before adding it to the trending catalogue."}
                      </span>
                    </div>
                  </div>
                </div>
                
                {/* Listing Assets & Verification Docs */}
                {selectedProp.images && selectedProp.images.length > 0 && (
                  <div className="space-y-2 border-t border-border/40 pt-4">
                    <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Gallery Images</Label>
                    <div className="grid grid-cols-4 gap-2">
                      {selectedProp.images.map((img, idx) => {
                        const rawUrl = typeof img === "string" ? img : img?.url || img?.public_url;
                        if (!rawUrl) return null;
                        const imgUrl = prefixUrl(rawUrl);
                        if (!imgUrl) return null;
                        return (
                          <div key={idx} className="rounded-xl overflow-hidden border border-border/60 aspect-square bg-muted relative">
                            <img src={imgUrl} alt="Property" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {selectedProp.documents && selectedProp.documents.length > 0 && (
                  <div className="space-y-2 border-t border-border/40 pt-4">
                    <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Verification Documents</Label>
                    <div className="grid grid-cols-2 gap-2">
                      {selectedProp.documents?.map((doc) => {
                        const rawUrl = doc?.url || doc?.public_url;
                        if (!rawUrl) return null;
                        const docUrl = prefixUrl(rawUrl);
                        if (!docUrl) return null;
                        return (
                          <a
                            key={doc.id}
                            href={docUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-between p-2.5 rounded-xl border border-border bg-muted/30 hover:bg-muted/70 transition text-xs"
                          >
                            <span className="font-semibold text-primary capitalize">{doc.document_type}</span>
                            <span className="text-[10px] text-emerald-500 font-bold uppercase">{doc.status || "uploaded"}</span>
                          </a>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="flex justify-end border-t border-border/50 pt-4 mt-6">
                  <Button type="submit" disabled={busy} className="font-bold">
                    {busy ? "Saving..." : "Update Property Details"}
                  </Button>
                </div>
              </form>
            ) : (
              <div className="text-center py-20 text-muted-foreground flex flex-col items-center gap-2">
                <Home className="w-10 h-10 text-muted-foreground/60" />
                <p>Select a property listing to review listing parameters and publish verification status.</p>
              </div>
            )
          )}

          {/* PLOT DEMANDS DETAILS & HISTORICAL EVENTS */}
          {activeTab === "plots" && (
            selectedDemand ? (
              <div className="space-y-6 animate-fade-up">
                <div className="border-b border-border/50 pb-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <h2 className="text-lg font-bold text-foreground">Plots on Demand Details</h2>
                      <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                        <MapPin className="w-3.5 h-3.5"/>
                        <span>{selectedDemand.location}</span>
                      </div>
                    </div>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded capitalize ${
                      selectedDemand.status === "COMPLETED" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"
                    }`}>
                      {selectedDemand.status}
                    </span>
                  </div>
                </div>

                {/* Plot Demand Specs Grid */}
                <div className="grid grid-cols-2 gap-4 bg-muted/20 p-4 rounded-xl border border-border/40">
                  <div className="text-xs">
                    <span className="text-muted-foreground block font-medium">Budget Limit</span>
                    <span className="text-sm font-bold text-emerald-600">{selectedDemand.budget != null ? formatPrice(selectedDemand.budget) : "Unavailable"}</span>
                  </div>
                  <div className="text-xs">
                    <span className="text-muted-foreground block font-medium">Required Area</span>
                    <span className="text-sm font-bold text-foreground">{formatArea(selectedDemand.area, selectedDemand.area_unit || "sqyd")}</span>
                  </div>
                  <div className="text-xs">
                    <span className="text-muted-foreground block font-medium">Buying Purpose</span>
                    <span className="text-sm font-semibold capitalize text-foreground">{selectedDemand.purpose || "—"}</span>
                  </div>
                  <div className="text-xs">
                    <span className="text-muted-foreground block font-medium">Timeframe</span>
                    <span className="text-sm font-semibold capitalize text-foreground">{selectedDemand.timeframe || "—"}</span>
                  </div>
                </div>

                {/* Buyer Reference Section */}
                <div className="bg-card border border-border rounded-xl p-4 space-y-3">
                  <h3 className="text-xs font-bold uppercase tracking-widest text-muted-foreground border-b border-border/40 pb-2">Buyer Reference</h3>
                  
                  {selectedDemand.buyer_name ? (
                    <div className="space-y-2 text-xs">
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground">Buyer Profile</span>
                        <button 
                          type="button"
                          onClick={() => {
                            navigate(`/dashboard/admin/users?select=${selectedDemand.buyer_user_id}`);
                          }}
                          title="Click to view buyer profile"
                          className="font-semibold text-primary hover:text-primary-hover hover:underline cursor-pointer text-right"
                        >
                          {selectedDemand.buyer_name}
                        </button>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground">Phone</span>
                        <span className="font-mono text-primary font-semibold">{selectedDemand.buyer_phone || "—"}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground">Email</span>
                        <span className="font-mono text-primary font-semibold truncate max-w-[200px]">{selectedDemand.buyer_email || "—"}</span>
                      </div>
                      {selectedDemand.lead_id && (
                        <div className="flex justify-between items-center border-t border-border/20 pt-1.5 mt-1.5">
                          <span className="text-muted-foreground">CRM Lead</span>
                          <Link 
                            to={`/dashboard/leads?select=${selectedDemand.lead_id}`}
                            title="Click to view lead in CRM pipeline"
                            className="font-semibold text-blue-400 hover:text-blue-300 hover:underline flex items-center gap-1"
                          >
                            View in CRM <ArrowRight className="w-3 h-3" />
                          </Link>
                        </div>
                      )}
                      {selectedDemand.assigned_agent_id && (
                        <div className="flex justify-between items-center border-t border-border/20 pt-1.5 mt-1.5">
                          <span className="text-muted-foreground">Assigned Agent</span>
                          <button
                            type="button"
                            onClick={() => {
                              navigate(`/dashboard/admin/users?select=${selectedDemand.assigned_agent_id}`);
                            }}
                            title="Click to view agent profile"
                            className="font-semibold text-primary hover:text-primary-hover hover:underline cursor-pointer text-right"
                          >
                            {selectedDemand.agent_name || "View Profile"}
                          </button>
                        </div>
                      )}
                      {selectedDemand.assigned_office_id && (
                        <div className="flex justify-between items-center border-t border-border/20 pt-1.5 mt-1.5">
                          <span className="text-muted-foreground">Assigned Office</span>
                          <button
                            type="button"
                            onClick={() => {
                              navigate(`/dashboard/admin/offices?select=${selectedDemand.assigned_office_id}`);
                            }}
                            title="Click to view office details"
                            className="font-semibold text-primary hover:text-primary-hover hover:underline cursor-pointer text-right"
                          >
                            {offices.find(o => String(o.office_id || o.id) === String(selectedDemand.assigned_office_id))?.name || "View Office"}
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-xs text-muted-foreground flex items-center gap-1.5 p-2 bg-amber-500/10 text-amber-500 rounded border border-amber-500/20">
                      <AlertCircle className="w-4 h-4 shrink-0" />
                      <span>This Plots on Demand request does not have an associated registered buyer profile (anonymous guest request).</span>
                    </div>
                  )}
                </div>

                {/* Assign Agent & Update Status Action Form */}
                <form onSubmit={handleDemandSave} className="bg-card border border-border rounded-xl p-4 space-y-4">
                  <h3 className="text-xs font-bold uppercase tracking-widest text-muted-foreground border-b border-border/40 pb-2">Assign &amp; Status Controls</h3>
                  
                  <div className="grid gap-4 md:grid-cols-3">
                    <div className="grid gap-1.5">
                      <Label htmlFor="det-d-status">Status</Label>
                      <select
                        id="det-d-status"
                        value={String(selectedDemand.status || "NEW").toUpperCase()}
                        onChange={(e) => setSelectedDemand({ ...selectedDemand, status: e.target.value })}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      >
                        <option value="NEW">New</option>
                        <option value="MATCHING">Matching</option>
                        <option value="ASSIGNED">Assigned</option>
                        <option value="COMPLETED">Completed</option>
                        <option value="CANCELLED">Cancelled</option>
                      </select>
                    </div>

                    <div className="grid gap-1.5">
                      <Label htmlFor="det-d-office">Assign Office</Label>
                      <select
                        id="det-d-office"
                        value={selectedDemand.assigned_office_id || ""}
                        onChange={(e) => setSelectedDemand({ ...selectedDemand, assigned_office_id: e.target.value, assigned_agent_id: "" })}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      >
                        <option value="">-- Select Office --</option>
                        {offices.map(o => (
                          <option key={o.office_id || o.id} value={o.office_id || o.id}>{o.name || o.office_name} ({o.city})</option>
                        ))}
                      </select>
                    </div>

                    <div className="grid gap-1.5">
                      <Label htmlFor="det-d-agent">Assign Agent</Label>
                      <select
                        id="det-d-agent"
                        value={selectedDemand.assigned_agent_id || ""}
                        onChange={(e) => setSelectedDemand({ ...selectedDemand, assigned_agent_id: e.target.value })}
                        disabled={!selectedDemand.assigned_office_id}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      >
                        <option value="">-- No Agent --</option>
                        {users.filter(u => String(u.role).toUpperCase() === "AGENT" && String(u.status).toUpperCase() === "ACTIVE" && u.verified_owner === true && String(u.office_id || u.raw?.office_id) === String(selectedDemand.assigned_office_id)).map(u => (
                          <option key={u.id || u.user_id} value={u.id || u.user_id}>{u.name} ({u.email})</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="grid gap-1.5">
                    <Label htmlFor="det-d-note">Admin note {String(selectedDemand.status || "").toUpperCase() === "CANCELLED" ? "(required)" : "(optional)"}</Label>
                    <textarea
                      id="det-d-note"
                      value={demandAdminNote}
                      onChange={(e) => setDemandAdminNote(e.target.value)}
                      maxLength={500}
                      rows={3}
                      placeholder="Record assignment context or the cancellation reason"
                      className="w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm outline-none transition-colors duration-200 ease-out focus:ring-2 focus:ring-ring"
                    />
                  </div>

                  <div className="flex justify-end pt-2">
                    <Button type="submit" disabled={busy || (String(selectedDemand.status || "").toUpperCase() === "ASSIGNED" && !selectedDemand.assigned_agent_id)} className="font-bold">
                      {busy ? "Updating..." : "Save Assignment Changes"}
                    </Button>
                  </div>
                </form>

                {/* Plot demand Recent Conversation timeline */}
                <div className="bg-card border border-border rounded-xl p-4 space-y-3">
                  <h3 className="text-xs font-bold uppercase tracking-widest text-muted-foreground border-b border-border/40 pb-2 flex items-center gap-1.5">
                    <PhoneCall className="w-4 h-4 text-muted-foreground" /> Conversations &amp; Inquiries History Log
                  </h3>
                  
                  <div className="space-y-3 max-h-60 overflow-y-auto pr-1">
                    {demandEvents.length === 0 ? (
                      <p className="text-xs text-muted-foreground italic">No conversations or inquiries logged for this buyer yet.</p>
                    ) : (
                      demandEvents.map((e) => (
                        <div key={e.id} className="text-xs bg-muted/40 p-3 rounded-lg border border-border/50 space-y-1.5">
                          <div className="flex justify-between items-center text-[10px] text-muted-foreground font-mono">
                            <span className="font-semibold text-foreground flex items-center gap-1">
                              {e.event_type === "CALL_LOG" ? (
                                <PhoneCall className="w-3.5 h-3.5 text-emerald-500 shrink-0"/>
                              ) : (
                                <MessageSquare className="w-3.5 h-3.5 text-blue-500 shrink-0"/>
                              )}
                              {e.actor_name || "System"}
                            </span>
                            <span>{new Date(e.created_at).toLocaleString("en-IN")}</span>
                          </div>
                          <p className="text-foreground whitespace-pre-wrap leading-relaxed">{cleanNoteText(e.note, e.actor_name)}</p>
                        </div>
                      ))
                    )}
                  </div>
                </div>

              </div>
            ) : (
              <div className="text-center py-20 text-muted-foreground flex flex-col items-center gap-2">
                <MapPinned className="w-10 h-10 text-muted-foreground/60" />
                <p>Select a Plots on Demand request from the list to view specifications, assigned agent, and conversation logs.</p>
              </div>
            )
          )}

        </div>
      </div>

      <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent className="sm:max-w-md" data-testid="admin-delete-dialog">
          <DialogHeader>
            <DialogTitle>{deleteTarget?.kind === "office" ? "Delete this office?" : "Permanently delete this listing?"}</DialogTitle>
            <DialogDescription>
              {deleteTarget?.kind === "office"
                ? `${deleteTarget?.label || "This office"} will be deleted only if the backend confirms it is safe to remove.`
                : `The unused listing “${deleteTarget?.label || "Untitled"}” will be permanently deleted. This cannot be undone.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDeleteTarget(null)}>Keep record</Button>
            <Button type="button" variant="destructive" disabled={busy} onClick={deleteTarget?.kind === "office" ? handleOfficeDelete : handlePropDelete}>{busy ? "Deleting…" : "Delete permanently"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Shared dialog provides focus trapping, Escape handling, and focus restoration. */}
      <Dialog open={blockModalOpen && !!selectedUser} onOpenChange={(open) => !open && setBlockModalOpen(false)}>
        <DialogContent className="sm:max-w-md" data-testid="block-unblock-modal">
            <DialogHeader>
            <DialogTitle className="flex items-center gap-3 text-amber-500">
              <AlertTriangle className="w-6 h-6 shrink-0" />
                {blockModalAction === "unblock" ? "Confirm Account Unblock" : "Confirm Account Block"}
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground leading-relaxed">
              Are you sure you want to {blockModalAction === "unblock" ? "unblock" : "block"} user{" "}
              <strong className="text-foreground">{selectedUser?.name || selectedUser?.email}</strong> (
              <span className="font-mono">{selectedUser?.email}</span>)?
              {blockModalAction === "block"
                ? " The user will be immediately logged out and unable to log in until unblocked."
                : " The user will be restored to ACTIVE status and able to log in immediately."}
            </DialogDescription>
            </DialogHeader>
            {blockModalAction === "block" && (
              <div className="grid gap-1.5 pt-1">
                <Label htmlFor="block-reason" className="text-xs">Reason (optional — shared with the user)</Label>
                <textarea
                  id="block-reason"
                  value={blockReason}
                  onChange={(e) => setBlockReason(e.target.value)}
                  rows={3}
                  placeholder="e.g. Repeated policy violations"
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm resize-none"
                  data-testid="block-reason-input"
                />
              </div>
            )}
            <DialogFooter className="gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setBlockModalOpen(false)}
                disabled={busy}
                className="text-xs font-semibold"
                data-testid="cancel-block-modal-btn"
              >
                Cancel
              </Button>

              <Button
                type="button"
                onClick={handleUserBlockAction}
                disabled={busy}
                className={`text-xs font-bold ${
                  blockModalAction === "unblock"
                    ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                    : "bg-destructive hover:bg-destructive/90 text-destructive-foreground"
                }`}
                data-testid="confirm-block-modal-btn"
              >
                {busy ? "Processing..." : blockModalAction === "unblock" ? "Yes, Unblock User" : "Yes, Block User"}
              </Button>
            </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
